import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { acesso } from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Skeleton } from '@/shared/ui/Skeleton';

import { PublicFrame } from './PublicFrame';
import { POLITICA_DE_SENHA } from './senha';

/**
 * Troca de senha pelo link (o /alterar-senha/:token do OnPoint).
 *
 * O segredo do endereco e a credencial da visita. O link serve uma vez, vence em
 * pouco tempo e aguenta poucas tentativas recusadas; toda recusa do link tem a mesma
 * resposta. A troca encerra todas as sessoes abertas, inclusive a de quem pediu: a
 * tela diz isso, porque quem troca a senha por suspeita precisa saber que o acesso
 * antigo caiu.
 */
export function PasswordResetPage() {
  const { token = '' } = useParams();

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);

  const consulta = useQuery({
    queryKey: ['acesso', 'troca-de-senha', token],
    queryFn: ({ signal }) => acesso.resetPreview(token, signal),
    retry: false,
    staleTime: Infinity,
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void trocar();
  }

  async function trocar() {
    setErro(null);
    if (senha !== confirmacao) {
      setErro('As duas senhas precisam ser iguais.');
      return;
    }
    setEnviando(true);
    try {
      await acesso.resetPassword(token, senha);
      setConcluido(true);
    } catch (causa) {
      if (causa instanceof ApiError && causa.code === ErrorCode.NotFound) {
        setIndisponivel(true);
      } else if (
        causa instanceof ApiError &&
        causa.code === ErrorCode.ValidationFailed
      ) {
        setErro(causa.message);
      } else if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
        setErro('Muitas tentativas em pouco tempo. Aguarde um instante.');
      } else {
        setErro('Nao foi possivel trocar a senha. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PublicFrame portal={null}>
      <h1 className="text-xl font-semibold text-ink-900">Criar uma senha nova</h1>

      {consulta.isPending ? (
        <div className="mt-6 flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : consulta.isError || indisponivel ? (
        <div className="mt-6 flex flex-col gap-4">
          <Alert tone="danger" title="Link indisponivel">
            Este link de troca de senha nao esta mais disponivel. Ele pode ter vencido,
            sido usado, substituido por um pedido mais novo ou esgotado as tentativas.
          </Alert>
          <Link
            to="/recuperar-senha"
            className="text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Pedir um link novo
          </Link>
        </div>
      ) : concluido ? (
        <div className="mt-6 flex flex-col gap-4">
          <Alert tone="success" title="Senha alterada">
            A senha nova ja vale. Por seguranca, todas as sessoes abertas com a senha
            antiga foram encerradas.
          </Alert>
          <Link
            to="/entrar"
            className="text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Ir para o acesso
          </Link>
        </div>
      ) : (
        <form onSubmit={enviar} noValidate className="mt-6 flex flex-col gap-4">
          {erro ? <Alert tone="danger">{erro}</Alert> : null}

          <Field label="E-mail" value={consulta.data.email} readOnly disabled />
          <Field
            label="Nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={senha}
            hint={POLITICA_DE_SENHA}
            onChange={(evento) => setSenha(evento.target.value)}
          />
          <Field
            label="Confirmar nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={confirmacao}
            onChange={(evento) => setConfirmacao(evento.target.value)}
          />

          <Button type="submit" size="lg" loading={enviando} className="mt-2 w-full">
            Salvar nova senha
          </Button>
        </form>
      )}
    </PublicFrame>
  );
}
